import { useEffect } from "react";
import { getAppParams } from "../utils/appParams";
import useQuery from "./use-query";
import useAuthStore from "../store/authStore";
import {
  fetchBlobBySharePointIds,
  fetchSharePointItemBlobFromUrl,
  fetchSharePointItemBlobByDriveRef,
} from "../utils/graph";

/**
 * useAuth hook - handles PDF source resolution and loading state.
 */
function useAuth() {
  const authStore = useAuthStore();

  let query = useQuery();
  const urlParam = query.get("url");
  const userToken = query.get("userToken");
  const nameParam = query.get("name");
  
  // SharePoint IDs for direct Graph access
  const siteIdParam = query.get("siteId");
  const listIdParam = query.get("listId");
  const listItemIdParam = query.get("listItemId");
  const driveIdParam = query.get("driveId");
  const itemIdParam = query.get("itemId");

  // Store context params for later use
  const tableParam = query.get("table");
  const sysIdParam = query.get("sys_id");
  const folderIdParam = query.get("folderId");
  const folderNameParam = query.get("folderName");
  const parentNumberParam = query.get("parentNumber");
  const userSysIdParam =
    query.get("user_sys_id") || query.get("userSysId") || query.get("sn_user_sys_id");

  useEffect(() => {
    console.log("[Auth] useEffect: URL params", { 
      urlParam, 
      userToken: userToken ? `present (${userToken.length} chars)` : "missing", 
      nameParam,
      siteIdParam, listIdParam, listItemIdParam, driveIdParam, itemIdParam
    });
    console.log("[Auth] Context params", { tableParam, sysIdParam, folderIdParam, folderNameParam, parentNumberParam });

    // Validate that we have a source to fetch the PDF
    const hasSource = Boolean(urlParam || (siteIdParam && listIdParam && listItemIdParam) || (driveIdParam && itemIdParam));
    if (!hasSource) {
      console.error("[Auth] No PDF source provided");
      authStore.setError({
        code: "NO_PDF_SOURCE",
        message: "No PDF source provided. Please open this from Smart Folders.",
        details: {
          urlParam,
          siteIdParam,
          listIdParam,
          listItemIdParam,
          driveIdParam,
          itemIdParam,
          nameParam,
        },
      });
      return;
    }
    authStore.clearError();

    // Store upload context in localStorage for save operations
    try {
      const ctx = {
        table: tableParam || '',
        sys_id: sysIdParam || '',
        folderId: folderIdParam || '',
        folderName: folderNameParam || '',
        parentNumber: parentNumberParam || '',
        user_sys_id: userSysIdParam || '',
        name: nameParam || ''
      };
      localStorage.setItem('sf_upload_ctx', JSON.stringify(ctx));
      console.log("[Auth] Stored upload context:", ctx);
    } catch (_) {}

  }, [urlParam, userToken, nameParam, tableParam, sysIdParam, folderIdParam, folderNameParam, parentNumberParam, userSysIdParam, siteIdParam, listIdParam, listItemIdParam, driveIdParam, itemIdParam]);

  /**
   * Fetch PDF from SharePoint via Microsoft Graph
   * Uses ServiceNow's sharepoint_token endpoint to get Graph access token
   */
  const fetchPdfFromSharePoint = () => {
    return new Promise(async (resolve) => {
      try {
        console.log("[Auth] fetchPdfFromSharePoint: start");
        authStore.setLoading(true);
        authStore.clearError();

        let blob = null;
        
        // Get params from URL
        const params = getAppParams();
        const siteId = params.get("siteId");
        const listId = params.get("listId");
        const listItemId = params.get("listItemId");
        const driveId = params.get("driveId");
        const itemId = params.get("itemId");
        const url = params.get("url");

        // Try different fetch methods based on available params
        if (url) {
          // Storage-agnostic dispatch: a pre-authorized direct URL
          // (e.g. an Azure Blob SAS from Lore) needs no Graph lookup —
          // fetch it as-is. Only SharePoint URLs go through the Graph
          // /shares resolution. Fixes "Graph shares lookup failed:
          // 403 Forbidden" for non-SharePoint documents (2026-07-17).
          let parsed = null;
          try { parsed = new URL(url); } catch (_) { parsed = null; }
          const isSharePoint = parsed && /(^|\.)sharepoint(-df)?\.com$/i.test(parsed.hostname);
          if (parsed && !isSharePoint) {
            console.log("[Auth] Direct fetch (non-SharePoint):", parsed.origin + parsed.pathname);
            const resp = await fetch(url);
            if (!resp.ok) {
              throw new Error(`Direct fetch failed: ${resp.status} ${resp.statusText}`);
            }
            blob = await resp.blob();
          } else {
            const decodedUrl = decodeURIComponent(url);
            console.log("[Auth] Using URL shares path:", decodedUrl);
            blob = await fetchSharePointItemBlobFromUrl(decodedUrl);
          }
        } else if (siteId && listId && listItemId) {
          console.log("[Auth] Using SharePoint IDs path");
          blob = await fetchBlobBySharePointIds({ siteId, listId, listItemId });
        } else if (driveId && itemId) {
          console.log("[Auth] Using drive+item path");
          blob = await fetchSharePointItemBlobByDriveRef(driveId, itemId);
        }

        if (!blob) {
          throw new Error("No PDF source provided or could not fetch file");
        }

        authStore.setFileBlob(blob);
        authStore.setLoading(false);
        console.log("[Auth] fetchPdfFromSharePoint: success, blob size:", blob.size);
        resolve(blob);
      } catch (error) {
        console.error("[Auth] fetchPdfFromSharePoint: error", error);
        authStore.setLoading(false);
        authStore.setError({
          code: "FETCH_PDF_FAILED",
          message: `Error fetching PDF: ${error?.message || String(error)}`,
          error,
          details: {
            url: window?.location?.href,
          },
        });
        resolve(null);
      }
    });
  };

  /**
   * Upload PDF to ServiceNow (placeholder - actual implementation is in PdfEditor.js)
   */
  const uploadPdfToSharePoint = async (fileBlob, fileName) => {
    console.warn("[Auth] uploadPdfToSharePoint called - use PdfEditor upload methods instead");
    return null;
  };

  return {
    accessToken: authStore.accessToken,
    loading: authStore.loading,
    fetchPdfFromSharePoint,
    uploadPdfToSharePoint,
    fileBuffer: authStore.fileBuffer,
    fileBlob: authStore.fileBlob,
    userToken: authStore.accessToken || sessionStorage.getItem("sn_user_token"),
    authError: authStore.error,
    clearAuthError: authStore.clearError,
  };
}

export default useAuth;
